// Deliberate duplicate of town-square/src/hooks/usePinnedActions.ts (ADR 0002). A change here belongs there too.
// Client divergences: react-i18next, `useAnalytics().amplitude`, the member id from
// `useAuth().user`, the two documents live in `~/lib/graphql/arty-card`, and no
// `cache.modify` on write: client keys `User` by `asfId`, so `Query.user` is a
// reference and the `updateUser` result merges into that same entity.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useAnalytics } from '~/context/analytics';
import { useAuth } from '~/context/auth';
import {
  applyPinOps,
  currentPins,
  listKnown,
  readStoredPins,
  settledPending,
  slotFor,
  writeStoredPins,
  type PinOp,
  type PinSlot,
} from '~/lib/arty-actions/pins-storage';
import { DEFAULT_PINS, MAX_PINS, effectivePins, isCustomPins, normalizePins } from '~/lib/arty-actions/pins';
import { OWNER_PINNED_ACTIONS_QUERY, UPDATE_PINNED_ACTIONS_MUTATION } from '~/lib/graphql/arty-card';

export type PinnedActionsState = {
  /** Effective shortcuts, in star order - defaults when the artist never customised. */
  pins: string[];
  isCustom: boolean;
  atCap: boolean;
  /** `live`: the pinned ids that still point at an action; any other pin is dropped on write. */
  toggle: (id: string, live?: readonly string[]) => void;
  reset: () => void;
};

const NONE: PinOp[] = [];

function idOf(value: unknown): string | null {
  if (value == null || value === '') {
    return null;
  }
  return String(value);
}

/**
 * The artist's pinned shortcuts, on the member: `User.pinnedActionIds` is the
 * record, so pins follow them across devices and apps.
 *
 * - Member: from the auth session, so a tap writes to the server even before
 *   the owner query lands.
 * - Read: a change still in flight, then the owner query, then this browser's
 *   copy of this member's pins (`arty.pins:<asfId>`). Until the network answers,
 *   that copy beats a cached owner read, which can predate this member's last save.
 * - Early tap: until the member and their list are known (this browser's copy
 *   or the owner query), a tap is held as the tap, shown on the current list,
 *   then replayed onto the member's real list and sent. A replayed pin past
 *   the cap is dropped with the cap toast.
 * - Write: optimistic through `updateUser`, normalized onto the cached
 *   `User:<asfId>` so the next read sees it; on failure the list shown before
 *   the tap is put back.
 * - Mirror: once the owner query is this member's, what the card shows is
 *   copied to that member's key.
 * - Seed: an account that never saved pins (`null`) while this browser holds a
 *   valid list for that same member gets it written once.
 */
export function usePinnedActions(isSignedIn: boolean): PinnedActionsState {
  const { t } = useTranslation('arty-card');
  const { amplitude } = useAnalytics();
  const { user } = useAuth();
  const [slot, setSlot] = useState<PinSlot>({ asfId: null, local: null, pending: null });
  const seeded = useRef<string | null>(null);

  const { data, loading, error } = useQuery(OWNER_PINNED_ACTIONS_QUERY, {
    skip: !isSignedIn,
    fetchPolicy: 'cache-and-network',
  });
  const [mutate] = useMutation(UPDATE_PINNED_ACTIONS_MUTATION);
  const owner = idOf(data?.user?.asfId);
  const asfId = idOf(user?.user_id) ?? owner;
  const loaded = Boolean(asfId) && owner === asfId;
  const fresh = loaded && !loading && !error;
  const ready = slot.asfId === asfId;
  const raw = loaded ? data?.user?.pinnedActionIds : null;
  const server = useMemo(() => {
    return normalizePins(raw);
  }, [raw]);
  const local = ready ? slot.local : null;
  const pending = ready ? slot.pending : null;
  const held = (ready && slot.held) || NONE;
  const stored = currentPins(pending, server, local, fresh);
  const known = ready && listKnown(asfId, local, loaded);
  const shown = useMemo(() => {
    return applyPinOps(stored, held).pins;
  }, [stored, held]);

  useEffect(() => {
    if (ready) {
      return;
    }
    setSlot((current) => {
      return slotFor(current, asfId, readStoredPins(asfId));
    });
  }, [asfId, ready]);

  useEffect(() => {
    const settled = settledPending(pending, server);
    if (settled === pending) {
      return;
    }
    setSlot((current) => {
      return { ...current, pending: settled };
    });
  }, [pending, server]);

  useEffect(() => {
    if (!ready || !fresh || !stored) {
      return;
    }
    writeStoredPins(asfId, stored);
  }, [ready, fresh, asfId, stored]);

  const write = useCallback(
    async (next: string[], previous: string[] | null) => {
      setSlot({ asfId, local: next, pending: next });
      writeStoredPins(asfId, next);
      if (!asfId) {
        return;
      }
      try {
        await mutate({
          variables: { input: { asfId, pinnedActionIds: next } },
          optimisticResponse: { updateUser: { __typename: 'User', asfId, pinnedActionIds: next } },
        });
      } catch {
        setSlot({ asfId, local: previous, pending: previous });
        writeStoredPins(asfId, previous);
        toast.error(t('toast.pins-failed'));
      }
    },
    [asfId, mutate, t]
  );

  useEffect(() => {
    if (!known || !held.length) {
      return;
    }
    const replayed = applyPinOps(stored, held);
    setSlot((current) => {
      return { ...current, held: undefined };
    });
    if (replayed.capped) {
      toast.error(t('toast.pin-cap', { max: MAX_PINS }));
    }
    if (replayed.pins === stored || !replayed.pins) {
      return;
    }
    void write(replayed.pins, stored);
  }, [known, held, stored, write, t]);

  useEffect(() => {
    if (!ready || !fresh || server || !local || pending || held.length || seeded.current === asfId) {
      return;
    }
    seeded.current = asfId;
    void write(local, local);
  }, [ready, fresh, server, local, pending, held, asfId, write]);

  const hold = useCallback((op: PinOp) => {
    setSlot((current) => {
      return { ...current, held: [...(current.held ?? []), op] };
    });
  }, []);

  const toggle = useCallback(
    (id: string, live?: readonly string[]) => {
      const op = { id, live };
      const next = applyPinOps(shown, [op]);
      if (next.capped || !next.pins) {
        toast.error(t('toast.pin-cap', { max: MAX_PINS }));
        return;
      }
      amplitude.track('arty_action_pin_toggled', { action_id: id, pinned: next.pins.includes(id) });
      // A held tap replays onto the member's real list, which the placeholder's live set
      // knows nothing about, so it carries only the id: dead-pin pruning is a live tap's job.
      if (!known) {
        hold({ id });
        return;
      }
      void write(next.pins, stored);
    },
    [shown, known, stored, hold, write, t, amplitude]
  );

  const reset = useCallback(() => {
    if (!known) {
      hold({ reset: true });
      return;
    }
    void write([...DEFAULT_PINS], stored);
  }, [known, stored, hold, write]);

  return useMemo(() => {
    const pins = effectivePins(shown);
    return { pins, isCustom: isCustomPins(shown), atCap: pins.length >= MAX_PINS, toggle, reset };
  }, [shown, toggle, reset]);
}
